'use client';

import { getAnalytics, logEvent, setUserId, setUserProperties, Analytics } from 'firebase/analytics';
import { app } from '@/lib/firebase/config';

let analytics: Analytics | null = null;

// Initialize analytics (only in browser)
function getAnalyticsInstance(): Analytics | null {
  if (typeof window === 'undefined') return null;

  if (!analytics) {
    try {
      analytics = getAnalytics(app);
    } catch (error) {
      console.warn('Analytics not available:', error);
      return null;
    }
  }

  return analytics;
}

// ===========================================
// USER IDENTIFICATION
// ===========================================

export function identifyUser(userId: string, properties?: {
  plan?: string;
  childCount?: number;
}) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  setUserId(instance, userId);

  if (properties) {
    setUserProperties(instance, {
      plan: properties.plan || 'free',
      child_count: String(properties.childCount || 0),
    });
  }
}

export function clearUserIdentity() {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  setUserId(instance, '');
}

// ===========================================
// PAGE VIEWS
// ===========================================

export function trackPageView(pageName: string, pageUrl?: string) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'page_view', {
    page_title: pageName,
    page_location: pageUrl || window.location.href,
  });
}

// ===========================================
// ARTWORK EVENTS
// ===========================================

export function trackArtworkUploaded(childId: string, fileType: string, fileSize: number) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'artwork_uploaded', {
    child_id: childId,
    file_type: fileType,
    file_size_kb: Math.round(fileSize / 1024),
  });
}

export function trackArtworkViewed(artworkId: string, childId: string) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'artwork_viewed', {
    artwork_id: artworkId,
    child_id: childId,
  });
}

export function trackArtworkDeleted(artworkId: string) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'artwork_deleted', {
    artwork_id: artworkId,
  });
}

// ===========================================
// STORY EVENTS
// ===========================================

export function trackStoryGenerated(params: {
  artworkId: string;
  childId: string;
  style: string;
  length: string;
  wordCount: number;
}) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'story_generated', {
    artwork_id: params.artworkId,
    child_id: params.childId,
    story_style: params.style,
    story_length: params.length,
    word_count: params.wordCount,
  });
}

export function trackStoryRegenerated(storyId: string, regenerationCount: number) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'story_regenerated', {
    story_id: storyId,
    regeneration_count: regenerationCount,
  });
}

export function trackStoryRead(storyId: string, wordCount: number) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'story_read', {
    story_id: storyId,
    word_count: wordCount,
  });
}

export function trackStoryRated(storyId: string, rating: number) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'story_rated', {
    story_id: storyId,
    rating: rating,
  });
}

export function trackStoryFavorited(storyId: string, favorited: boolean) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, favorited ? 'story_favorited' : 'story_unfavorited', {
    story_id: storyId,
  });
}

// ===========================================
// SHARING EVENTS
// ===========================================

export function trackShareCreated(type: 'story' | 'artwork', itemId: string) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'share_created', {
    share_type: type,
    item_id: itemId,
  });
}

export function trackShareViewed(shareToken: string) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'share_viewed', {
    share_token: shareToken,
  });
}

// ===========================================
// FAMILY EVENTS
// ===========================================

export function trackInvitationSent(childId: string) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'invitation_sent', {
    child_id: childId,
  });
}

export function trackInvitationAccepted() {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'invitation_accepted');
}

// ===========================================
// VOICE MEMO EVENTS
// ===========================================

export function trackVoiceMemoRecorded(artworkId: string, durationSeconds: number) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'voice_memo_recorded', {
    artwork_id: artworkId,
    duration_seconds: durationSeconds,
  });
}

// ===========================================
// CHILD PROFILE EVENTS
// ===========================================

export function trackChildCreated(childId: string) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'child_created', {
    child_id: childId,
  });
}

export function trackChildDeleted(childId: string) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'child_deleted', {
    child_id: childId,
  });
}

// ===========================================
// CONVERSION / ENGAGEMENT EVENTS
// ===========================================

export function trackSignUp(method: 'email' | 'google') {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'sign_up', {
    method: method,
  });
}

export function trackLogin(method: 'email' | 'google') {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'login', {
    method: method,
  });
}

export function trackFeatureUsed(featureName: string) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'feature_used', {
    feature_name: featureName,
  });
}

// ===========================================
// ERROR EVENTS
// ===========================================

export function trackError(errorType: string, errorMessage: string) {
  const instance = getAnalyticsInstance();
  if (!instance) return;

  logEvent(instance, 'error_occurred', {
    error_type: errorType,
    error_message: errorMessage.substring(0, 100), // Limit message length
  });
}
